import type { TaskListItem } from '@cg-projects/shared';
import { dueBucket, taskDate, type DateField } from './grouping';

export interface FilterOpts {
  search: string;
  assigneeId: number | null;
  /** Namespaced tag key: 'ct:<id>' (content) or 'st:<id>' (standard). */
  tagId?: string | null;
  /** Only tasks in this stage. The BOARD deliberately omits this from its
   *  column grouping and renders a focused single-stage view instead —
   *  passing it there would just draw every other column empty. */
  stageId?: number | null;
  onlyUnassigned?: boolean;
  dueThisWeek?: boolean;
  dateField?: DateField;
}

// Client-side filters on the loaded task set (search + assignee + focus chips).
// "Show completed" is handled server-side (it changes which tasks are fetched).
export function filterTasks(tasks: TaskListItem[], opts: FilterOpts): TaskListItem[] {
  const q = opts.search.trim().toLowerCase();
  const dateField = opts.dateField ?? 'date_deadline';
  if (!q && !opts.assigneeId && !opts.tagId && !opts.stageId && !opts.onlyUnassigned && !opts.dueThisWeek) return tasks;
  return tasks.filter((t) => {
    if (opts.stageId && t.stageId !== opts.stageId) return false;
    // Search matches what the user can SEE on the card: name, tags, assignees.
    if (q) {
      const hit =
        t.name.toLowerCase().includes(q) ||
        t.tags.some((x) => x.name.toLowerCase().includes(q)) ||
        t.contentTags.some((x) => x.name.toLowerCase().includes(q)) ||
        t.assignees.some((a) => a.name.toLowerCase().includes(q));
      if (!hit) return false;
    }
    if (opts.assigneeId && !t.assignees.some((a) => a.id === opts.assigneeId)) return false;
    if (opts.tagId) {
      const [ns, rawId] = opts.tagId.split(':');
      const id = Number(rawId);
      const hit = ns === 'ct' ? t.contentTags.some((x) => x.id === id) : t.tags.some((x) => x.id === id);
      if (!hit) return false;
    }
    if (opts.onlyUnassigned && t.assignees.length > 0) return false;
    if (opts.dueThisWeek) {
      const b = dueBucket(taskDate(t, dateField));
      if (b !== 'overdue' && b !== 'today' && b !== 'week') return false;
    }
    return true;
  });
}

/** Unique tags present across a task set, keyed by MODEL-namespaced id —
 *  content tags and standard tags have colliding numeric ids in live Odoo. */
export function uniqueTags(tasks: TaskListItem[]) {
  const map = new Map<string, { key: string; name: string }>();
  for (const t of tasks) {
    for (const x of t.contentTags) { const k = 'ct:' + x.id; if (!map.has(k)) map.set(k, { key: k, name: x.name }); }
    for (const x of t.tags) { const k = 'st:' + x.id; if (!map.has(k)) map.set(k, { key: k, name: x.name }); }
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Unique assignees present across a task set, sorted by name (for filter options). */
export function uniqueAssignees(tasks: TaskListItem[]) {
  const map = new Map<number, { id: number; name: string }>();
  for (const t of tasks) for (const a of t.assignees) if (!map.has(a.id)) map.set(a.id, { id: a.id, name: a.name });
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
}
